import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../components/ui/Button.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Modal } from '../../components/ui/Modal.js';
import { useApi } from '../../hooks/useApi.js';
import { useClientesLookup } from '../../hooks/useClientesLookup.js';
import { ApiError } from '../../lib/api.js';
import { fmtFechaDisplay, toDateInputValue } from '../../lib/dates.js';
import type { EntradaCxc, EntradaPrefill } from './types.js';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// value = estado_factura real del backend (packages/db/src/schema/cxc.ts);
// "cancelar" del mockup se corrige a "cancelada" para que coincida con el enum real.
const ESTADO_FACTURA_OPTIONS = [
  { value: 'solicitar', label: 'Solicitar factura' },
  { value: 'solicitada', label: 'Factura solicitada' },
  { value: 'emitida', label: 'XML + PDF disponibles' },
  { value: 'cancelada', label: 'Cancelar factura' },
];

const AUTORIZACION_OPTIONS = [
  { value: 'solicitar_factura', label: 'Solicitar factura' },
  { value: 'factura_solicitada', label: 'Factura solicitada' },
  { value: 'factura_cancelada', label: 'Factura cancelada' },
  { value: 'nota_de_credito', label: 'Nota de crédito' },
  { value: 'esperar_autorizacion', label: 'Esperar autorización' },
  { value: 'factura_enviada', label: 'Factura enviada' },
  { value: 'factura_vigente', label: 'Factura vigente' },
  { value: 'pago_atrasado', label: 'Pago atrasado' },
];

function addDays(dateStr: string, days: number): Date {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d;
}

function fmtMXN(n: number): string {
  return `$${n.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

interface NuevaEntradaModalProps {
  open: boolean;
  onClose: () => void;
  // Si se define, el modal opera en modo edición sobre esa entrada (PATCH) en
  // vez de crear una nueva (POST) — mismo patrón dual-modo de NuevoCliente.tsx.
  entradaId?: string | null;
  // Duplicar (mejoras CxC 2026-09-30): crea una entrada nueva precargada con
  // lo recurrente de otra, ya movida al mes siguiente. Ceci revisa y guarda.
  prefill?: EntradaPrefill | null;
}

export function NuevaEntradaModal({ open, onClose, entradaId, prefill }: NuevaEntradaModalProps) {
  const esEdicion = Boolean(entradaId);
  const { get, post, patch } = useApi();
  const queryClient = useQueryClient();

  const [clienteId, setClienteId] = useState('');
  const [estadoFactura, setEstadoFactura] = useState('solicitar');
  const [mes, setMes] = useState('');
  const [anio, setAnio] = useState('');
  const [fechaFactura, setFechaFactura] = useState('');
  const [monto, setMonto] = useState('');
  const [plazoDias, setPlazoDias] = useState('');
  const [pago, setPago] = useState('');
  const [fechaPago, setFechaPago] = useState('');
  const [autorizacionFactura, setAutorizacionFactura] = useState('');
  const [concepto, setConcepto] = useState('');
  // #75: cobro adicional al servicio (refacturación, comisiones…).
  const [esAdicional, setEsAdicional] = useState(false);
  const [promesaPago, setPromesaPago] = useState('');
  const [planAccion, setPlanAccion] = useState('');
  const [comentarios, setComentarios] = useState('');

  const [dirty, setDirty] = useState(false);
  const [apiError, setApiError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [confirmCerrarAbierto, setConfirmCerrarAbierto] = useState(false);

  const { data: clientesData } = useClientesLookup({ enabled: open });

  const { data: entradaExistente } = useQuery({
    queryKey: ['cxc', entradaId],
    queryFn: () => get<EntradaCxc>(`/cxc/${entradaId}`),
    enabled: open && esEdicion,
  });

  // Prompt: Ajustes finos, ítem 4 — una vez que la factura fue recibida (XML
  // real ya parseado por factura-imap-poll.job.ts, ver recibirFactura()), la
  // fecha deja de ser editable a mano. Antes de recibirla no hay valor aún,
  // así que sigue editable con el mismo criterio de siempre.
  const fechaFacturaBloqueada = esEdicion && Boolean(entradaExistente?.fecha_factura);

  // Precarga del formulario en modo edición — no marca dirty, para que cerrar
  // el modal sin tocar nada no dispare el ConfirmModal de "cambios sin guardar".
  useEffect(() => {
    if (!open || !esEdicion || !entradaExistente) return;
    setClienteId(entradaExistente.cliente_id);
    setEstadoFactura(entradaExistente.estado_factura);
    setMes(entradaExistente.mes);
    setAnio(String(entradaExistente.anio));
    setFechaFactura(toDateInputValue(entradaExistente.fecha_factura));
    setMonto(entradaExistente.monto);
    setPlazoDias(String(entradaExistente.plazo_dias));
    setPago(entradaExistente.pago);
    setFechaPago(toDateInputValue(entradaExistente.fecha_pago));
    setAutorizacionFactura(entradaExistente.autorizacion_factura);
    setConcepto(entradaExistente.concepto ?? '');
    setEsAdicional(entradaExistente.es_adicional);
    setPromesaPago(toDateInputValue(entradaExistente.promesa_pago));
    setPlanAccion(entradaExistente.plan_accion ?? '');
    setComentarios(entradaExistente.comentarios ?? '');
    setDirty(false);
  }, [open, esEdicion, entradaExistente]);

  // Precarga al duplicar — tampoco marca dirty: cerrar sin guardar no pierde
  // nada que Ceci haya escrito.
  useEffect(() => {
    if (!open || esEdicion || !prefill) return;
    reset(); // parte de cero aunque un cierre futuro no limpie el formulario
    setClienteId(prefill.cliente_id);
    setConcepto(prefill.concepto);
    setEsAdicional(prefill.es_adicional);
    setMes(prefill.mes);
    setAnio(String(prefill.anio));
    setMonto(prefill.monto);
    setPlazoDias(String(prefill.plazo_dias));
    setDirty(false);
  }, [open, esEdicion, prefill]);

  const vencimiento = useMemo(() => {
    const plazo = parseInt(plazoDias, 10);
    if (!fechaFactura || isNaN(plazo)) return null;
    return addDays(fechaFactura, plazo);
  }, [fechaFactura, plazoDias]);

  const facturadoConIva = useMemo(() => {
    const m = parseFloat(monto);
    return m > 0 ? m * 1.16 : null;
  }, [monto]);

  const estaVencida = useMemo(() => {
    if (!vencimiento) return false;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return vencimiento.getTime() < hoy.getTime();
  }, [vencimiento]);

  const vencidoConIva = pago === 'No' && estaVencida ? facturadoConIva : null;

  function reset() {
    setClienteId('');
    setEstadoFactura('solicitar');
    setMes('');
    setAnio('');
    setFechaFactura('');
    setMonto('');
    setPlazoDias('');
    setPago('');
    setFechaPago('');
    setAutorizacionFactura('');
    setConcepto('');
    setEsAdicional(false);
    setPromesaPago('');
    setPlanAccion('');
    setComentarios('');
    setDirty(false);
    setApiError('');
    setValidationError('');
  }

  const guardarMutation = useMutation({
    mutationFn: () => {
      if (esEdicion) {
        // actualizarEntradaSchema no incluye cliente_id ni estado_factura (se
        // gestionan aparte) — se envían valores explícitos (incl. null) para
        // que limpiar un campo opcional en el form sí lo limpie en BD.
        return patch(`/cxc/${entradaId}`, {
          mes,
          anio: Number(anio),
          fecha_factura: fechaFactura || null,
          monto: Number(monto),
          plazo_dias: Number(plazoDias),
          pago,
          fecha_pago: pago === 'Si' && fechaPago ? fechaPago : null,
          autorizacion_factura: autorizacionFactura,
          plan_accion: planAccion.trim() || null,
          concepto: concepto.trim() || null,
          es_adicional: esAdicional,
          promesa_pago: promesaPago || null,
          comentarios: comentarios.trim() || null,
        });
      }
      return post('/cxc', {
        cliente_id: clienteId,
        mes,
        anio: Number(anio),
        ...(fechaFactura ? { fecha_factura: fechaFactura } : {}),
        monto: Number(monto),
        ...(plazoDias ? { plazo_dias: Number(plazoDias) } : {}),
        pago,
        ...(pago === 'Si' && fechaPago ? { fecha_pago: fechaPago } : {}),
        estado_factura: estadoFactura,
        ...(autorizacionFactura ? { autorizacion_factura: autorizacionFactura } : {}),
        ...(concepto.trim() ? { concepto: concepto.trim() } : {}),
        es_adicional: esAdicional,
        ...(promesaPago ? { promesa_pago: promesaPago } : {}),
        ...(planAccion.trim() ? { plan_accion: planAccion.trim() } : {}),
        ...(comentarios.trim() ? { comentarios: comentarios.trim() } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cxc'] });
      reset();
      onClose();
    },
    onError: (err: unknown) => {
      setApiError(err instanceof ApiError ? err.message : `Ocurrió un error al ${esEdicion ? 'guardar los cambios' : 'registrar la entrada'}.`);
    },
  });

  function handleGuardar() {
    if (!clienteId || !mes || !anio || !monto) {
      setValidationError('Completa los campos obligatorios: Cliente, Mes, Año y Monto.');
      return;
    }
    // "Cobrado este mes" (CxCPage.tsx) atribuye el cobro al mes de fecha_pago
    // — sin fecha, la entrada quedaba marcada como pagada pero invisible en
    // ese cálculo. Mismo resguardo que actualizarEntradaSchema del backend.
    if (pago === 'Si' && !fechaPago) {
      setValidationError('Indica la Fecha de Pago: es obligatoria cuando ¿Pagó? es "Sí".');
      return;
    }
    setValidationError('');
    setApiError('');
    guardarMutation.mutate();
  }

  function handleAttemptClose() {
    if (dirty) {
      setConfirmCerrarAbierto(true);
      return;
    }
    reset();
    onClose();
  }

  function confirmarCierre() {
    setConfirmCerrarAbierto(false);
    reset();
    onClose();
  }

  if (!open) return null;

  return (
    <>
    <Modal
      open={open}
      title={esEdicion ? 'Editar Entrada' : prefill ? 'Duplicar Entrada (mes siguiente)' : 'Registrar Nueva Entrada'}
      onClose={handleAttemptClose}
      footer={
        <>
          <Button variant="secondary" onClick={handleAttemptClose}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={guardarMutation.isPending}>
            {guardarMutation.isPending ? 'Guardando…' : esEdicion ? 'Guardar Cambios' : 'Registrar Entrada'}
          </Button>
        </>
      }
    >
      {apiError && <div className="form-error-banner">{apiError}</div>}
      {validationError && <div className="form-error-banner">{validationError}</div>}

      <div className="fg">
        <div className="fs-title">Datos de Factura</div>
        <div className="ff">
          <label>Cliente *</label>
          <select
            value={clienteId}
            disabled={esEdicion}
            onChange={e => {
              setClienteId(e.target.value);
              setDirty(true);
            }}
          >
            <option value="">Seleccionar...</option>
            {(clientesData?.data ?? []).map(c => (
              <option key={c.id} value={c.id}>
                {c.nombre_comercial}
              </option>
            ))}
          </select>
          {esEdicion && <span className="ff-hint">El cliente de una entrada existente no se puede cambiar.</span>}
        </div>
        <div className="ff">
          <label>Estado de Factura</label>
          <select
            value={estadoFactura}
            disabled={esEdicion}
            onChange={e => {
              setEstadoFactura(e.target.value);
              setDirty(true);
            }}
          >
            {ESTADO_FACTURA_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {esEdicion && <span className="ff-hint">Se gestiona con Solicitar / Cancelar factura en la tabla.</span>}
        </div>
        <div className="ff full">
          <label>Concepto</label>
          <input
            type="text"
            maxLength={200}
            placeholder="SERVICIO OCTUBRE…"
            value={concepto}
            onChange={e => {
              setConcepto(e.target.value);
              setDirty(true);
            }}
          />
          <span className="ff-hint">Opcional. Se envía al contador en la solicitud de factura.</span>
          <label className="ff-check">
            <input
              type="checkbox"
              checked={esAdicional}
              onChange={e => {
                setEsAdicional(e.target.checked);
                setDirty(true);
              }}
            />
            ¿Es adicional al servicio? (no es tarifa de servicio: refacturación, comisiones…)
          </label>
        </div>
        <div className="ff">
          <label>Mes *</label>
          <select
            value={mes}
            onChange={e => {
              setMes(e.target.value);
              setDirty(true);
            }}
          >
            <option value="">Seleccionar...</option>
            {MESES.map(m => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="ff">
          <label>Año *</label>
          <input
            type="number"
            min={2020}
            max={2030}
            placeholder="2026"
            value={anio}
            onChange={e => {
              setAnio(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="ff">
          {/* F.6 §9 — obligatoria salvo cuando aún no existe la factura ('solicitar') */}
          <label>Fecha Factura{estadoFactura !== 'solicitar' ? ' *' : ''}</label>
          <input
            type="date"
            value={fechaFactura}
            disabled={fechaFacturaBloqueada}
            title={fechaFacturaBloqueada ? 'Fecha tomada del XML de la factura recibida — no editable' : undefined}
            onChange={e => {
              setFechaFactura(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="ff">
          <label>Monto Facturado (MXN) *</label>
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder="85,000.00"
            value={monto}
            onChange={e => {
              setMonto(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="ff">
          <label>Plazo (días) *</label>
          <input
            type="number"
            min={0}
            max={365}
            placeholder="30"
            value={plazoDias}
            onChange={e => {
              setPlazoDias(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="ff">
          <label>Vencimiento (calculado)</label>
          <div className="ff-calculated">{vencimiento ? fmtFechaDisplay(vencimiento.toISOString()) : 'Se calcula automáticamente'}</div>
        </div>

        <div className="fs-title">Pago y Estado</div>
        <div className="ff">
          <label>¿Pagó?</label>
          <select
            value={pago}
            onChange={e => {
              setPago(e.target.value);
              setDirty(true);
            }}
          >
            <option value="">—</option>
            <option value="Si">Sí</option>
            <option value="No">No</option>
          </select>
        </div>
        {pago === 'Si' && (
          <div className="ff">
            <label>Fecha de Pago *</label>
            <input
              type="date"
              value={fechaPago}
              onChange={e => {
                setFechaPago(e.target.value);
                setDirty(true);
              }}
            />
            <span className="ff-hint" style={{ marginTop: 3 }}>
              Timestamp al marcar como pagado
            </span>
          </div>
        )}
        <div className="ff">
          <label>Autorización Factura</label>
          <select
            value={autorizacionFactura}
            onChange={e => {
              setAutorizacionFactura(e.target.value);
              setDirty(true);
            }}
          >
            <option value="">Seleccionar...</option>
            {AUTORIZACION_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ff">
          <label>Promesa de pago</label>
          <input
            type="date"
            value={promesaPago}
            onChange={e => {
              setPromesaPago(e.target.value);
              setDirty(true);
            }}
          />
        </div>

        <div className="fs-title">Calculados (referencia)</div>
        <div className="ff">
          <label>Facturado c/IVA</label>
          <div className="ff-calculated">{facturadoConIva ? fmtMXN(facturadoConIva) : '—'}</div>
        </div>
        <div className="ff">
          <label>Vencido c/IVA</label>
          <div className="ff-calculated">{vencidoConIva ? fmtMXN(vencidoConIva) : '—'}</div>
        </div>

        <div className="fs-title">Seguimiento</div>
        <div className="ff full">
          <label>Plan de Acción</label>
          <textarea
            placeholder="Plan de cobranza si aplica…"
            value={planAccion}
            onChange={e => {
              setPlanAccion(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="ff full">
          <label>Comentarios</label>
          <textarea
            placeholder="Notas adicionales…"
            value={comentarios}
            onChange={e => {
              setComentarios(e.target.value);
              setDirty(true);
            }}
          />
        </div>
      </div>
    </Modal>

      <ConfirmModal
        open={confirmCerrarAbierto}
        title="¿Cancelar operación?"
        body="Tienes cambios sin guardar. Si cierras ahora, se perderán."
        confirmLabel="Descartar y cerrar"
        danger
        onConfirm={confirmarCierre}
        onCancel={() => setConfirmCerrarAbierto(false)}
      />
    </>
  );
}
